//@ts-check
import {Button} from '@/components-v2/atoms/Button/Button'
import {Notification} from '@/components-v2/molecules/Notification/Notification'
import {COUNTRY_CODES} from '@/utils/constants/countryConstants'
import {useState} from 'react'
import {DiscardChangesModal} from './DiscardChangesModal'
import {MAX_BULK_EDIT_SELECTION} from './constants'

/**
 * Collects every bulk-edit validation error into a single list of messages.
 * @param {{ hasMissingRequiredFields?: boolean; missingRequiredFieldColumns?: {header: string}[]; selectionCount?: number }} params
 * @returns {string[]}
 */
const getBulkEditErrors = ({hasMissingRequiredFields, missingRequiredFieldColumns = [], selectionCount = 0}) => {
  const errors = []

  if (hasMissingRequiredFields) {
    errors.push(`${missingRequiredFieldColumns.map(column => column.header).join(', ')} required`)
  }

  if (selectionCount > MAX_BULK_EDIT_SELECTION) {
    errors.push(`Bulk edit is limited to ${MAX_BULK_EDIT_SELECTION} products at a time.`)
  }

  return errors
}

/**
 * Bottom action bar for the v2 Product List page.
 * Mirrors ProductReviewActions - Edit/Cancel toggle, Save persists edits via the bulk edit API.
 * @param {{ isEditMode: boolean; isSaving?: boolean; changedRowsCount?: number;selectionCount?: number; hasMissingRequiredFields?: boolean; missingRequiredFieldColumns?: {header: string}[]; hasValidationErrors?: boolean; countryCode?: string; onEdit: () => void; onCancel: () => void; onSave: () => void }} props
 */
export const ProductListActions = ({
  isEditMode,
  isSaving = false,
  changedRowsCount = 0,
  selectionCount = 0,
  hasMissingRequiredFields = false,
  missingRequiredFieldColumns = [],
  hasValidationErrors = false,
  countryCode,
  onEdit,
  onCancel,
  onSave,
}) => {
  const [isDiscardModalOpen, setDiscardModalOpen] = useState(false)
  const isBlockedCountry = [COUNTRY_CODES.MX, COUNTRY_CODES.IND].includes(
    String(countryCode ?? '')
      .trim()
      .toUpperCase()
  )

  if (isBlockedCountry) return null

  const handleDiscardClick = () => {
    if (changedRowsCount > 0) {
      setDiscardModalOpen(true)
      return
    }
    onCancel()
  }

  const handleConfirmDiscard = () => {
    setDiscardModalOpen(false)
    onCancel()
  }

  const errors = getBulkEditErrors({hasMissingRequiredFields, missingRequiredFieldColumns, selectionCount})

  return (
    <div className="sticky bottom-0 top-0 z-[10] mt-4 flex items-center justify-between gap-3 bg-[var(--color-background-layer-01)] p-4">
      {!isEditMode ? (
        <>
          <div />
          <Button text="Edit" size="medium" variant="primary" iconLeading="edit" onClick={onEdit} />
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            {changedRowsCount > 0 && (
              <div className="min-w-40">
                {changedRowsCount > 0 && `${changedRowsCount} unsaved change${changedRowsCount === 1 ? '' : 's'}`}
              </div>
            )}
            {errors.length > 0 && (
              <Notification
                className="!p-1"
                type="warning"
                showText
                text={errors.join(' ')}
                showActionButtons={false}
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              text="Discard Changes"
              size="medium"
              variant="auxiliary"
              disabled={isSaving}
              onClick={handleDiscardClick}
            />
            <Button
              text="Save Changes"
              size="medium"
              variant="primary"
              isLoading={isSaving}
              loadingText="Saving"
              disabled={hasValidationErrors || errors.length > 0 || changedRowsCount === 0}
              onClick={onSave}
            />
          </div>
        </>
      )}
      <DiscardChangesModal
        showModal={isDiscardModalOpen}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setDiscardModalOpen(false)}
      />
    </div>
  )
}
